import { CodeFormat, OrderCode } from "@web-app/types";

/** One barcode row in an order form; quantity is kept as typed. */
export type ScannableCodeEntry = {
  code: string;
  /** Empty means the workspace default. */
  format: CodeFormat | "";
  isRequired: boolean;
  quantity: string;
};

export const emptyScannableCodeEntry = (): ScannableCodeEntry => ({
  code: "",
  format: "",
  isRequired: false,
  quantity: "1",
});

/** Returns an error message for the barcode rows, or true when they're valid. */
export function validateScannableCodeEntries(
  entries: ScannableCodeEntry[] | undefined,
): string | true {
  const seen = new Set<string>();
  for (const entry of entries ?? []) {
    const code = entry.code.trim();
    if (!code) return "Enter a code for each barcode, or remove the empty row.";
    if (seen.has(code)) return `Barcode "${code}" is listed more than once.`;
    seen.add(code);
    if (
      !/^\d+$/.test(entry.quantity.trim()) ||
      parseInt(entry.quantity, 10) < 1
    ) {
      return `Quantity for "${code}" must be a whole number of at least 1.`;
    }
  }
  return true;
}

/**
 * Converts barcode rows into a stop's codes. A blank format is sent as is and
 * web-api fills in the workspace default.
 */
export function toOrderCodes(
  entries: ScannableCodeEntry[] | undefined,
): OrderCode[] {
  return (entries ?? []).map((entry) => ({
    code: entry.code.trim(),
    format: entry.format,
    isRequired: entry.isRequired,
    quantity: parseInt(entry.quantity, 10),
  }));
}

/** Converts a stop's codes into editable rows, in their saved order. */
export function toScannableCodeEntries(
  codes: OrderCode[] | null | undefined,
): ScannableCodeEntry[] {
  return (codes ?? []).map((code) => ({
    code: code.code,
    format: code.format ?? "",
    isRequired: !!code.isRequired,
    quantity: String(code.quantity || 1),
  }));
}
